import type { Element } from 'bpmn-js/lib/model/Types';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import type { Injector } from 'didi';
import type { ServiceMap } from '../../types/didi';
import type {
    BpmnPropertiesPanelModule,
    GroupFn,
    Groups,
    PropertiesProvider as PropsProvider,
} from '../../types/properties-panel';
import { ProcessSpGroup } from '../properties-panel/components/ProcessSpGroup';
import { TaskMailsGroup } from '../properties-panel/components/TaskMailsGroup';
import { TaskPermissionsGroup } from '../properties-panel/components/TaskPermissionsGroup';
import { TaskSpGroup } from '../properties-panel/components/TaskSpGroup';

// import { isGroup, isListGroup } from '../properties-panel/util/group-util';

class PropertiesProvider {
    public static readonly $inject: string[] = ['propertiesPanel', 'injector'];

    private readonly _injector: Injector<ServiceMap>;

    constructor(propertiesPanel: BpmnPropertiesPanelModule, injector: Injector<ServiceMap>) {
        propertiesPanel.registerProvider(500, this);
        this._injector = injector;
        // console.log(injector);
    }

    getGroups: PropsProvider['getGroups'] = (element) => {
        return (groups) => {
            // remove groups
            groups = removeGroups(element, groups);

            // remove groups entries
            // groups = removeGroupsEntries(element, groups);

            // add groups
            groups = groups.concat(createGroups(this._injector, element, stepManGroups));

            // update groups...
            // TBD

            // move groups
            moveGroups(element, groups);

            return groups;
        };
    };
}

const filterOutGroupIds = (groups: Groups, ids: string[]): Groups =>
    groups.filter((group) => !ids.includes(group.id));

const removeGroups = (element: Element, groups: Groups): Groups => {
    const groupIds: string[] = [];

    switch (element.type) {
        case 'bpmn:EndEvent':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties',
                'CamundaPlatform__Input'
            );
            break;

        case 'bpmn:ExclusiveGateway':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties'
            );
            break;

        case 'bpmn:Process':
            groupIds.push(
                'CamundaPlatform__CandidateStarter',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties',
                'CamundaPlatform__ExternalTask',
                'CamundaPlatform__HistoryCleanup',
                'CamundaPlatform__JobExecution',
                'CamundaPlatform__Tasklist'
            );
            break;

        case 'bpmn:SequenceFlow':
            groupIds.push(
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties'
            );
            break;

        case 'bpmn:ServiceTask':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties',
                'CamundaPlatform__FieldInjection'
            );
            break;

        case 'bpmn:ScriptTask':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties'
            );
            break;

        case 'bpmn:StartEvent':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties',
                'CamundaPlatform__JobExecution',
                'CamundaPlatform__StartInitiator'
            );
            break;

        case 'bpmn:SubProcess':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties'
            );
            break;

        case 'bpmn:Task':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties'
            );
            break;

        case 'bpmn:UserTask':
            groupIds.push(
                'CamundaPlatform__AsynchronousContinuations',
                'CamundaPlatform__ExecutionListener',
                'CamundaPlatform__ExtensionProperties',
                'CamundaPlatform__TaskListener',
                'CamundaPlatform__UserAssignment'
            );
            break;
    }

    return groupIds.length > 0 ? filterOutGroupIds(groups, groupIds) : groups;
};

/*const removeGroupsEntries = (element: Element, groups: Groups): Groups =>
    groups.map((group) => removeGroupEntries(element, group));

const removeGroupEntries = <T extends Groups[number]>(element: Element, group: T): T => {
    console.log('### el', element);
    const removeEntries = <T extends EntryDefinition[]>(entries: T): T =>
        entries
            .map((entry) => {
                console.log('### entry', entry);
                switch (entry.id) {
                    //case 'isExecutable':
                    //    console.log('### remove group entry', entry.id, entry);
                    //    return undefined;
                    default:
                        return entry;
                }
            })
            .filter((entry) => entry) as T;

    if (isGroup(group)) {
        group.entries = removeEntries(group.entries);
    } else if (isListGroup(group)) {
        group.items = group.items
            .map((item) => {
                switch (item.id) {
                    // TODO: return undefined to ignore an entry of a list group
                    //case 'xy':
                    //    return undefined;
                    default:
                        break;
                }

                item.entries = removeEntries(item.entries);
                return item;
            })
            .filter((item) => item) as typeof group.items;
    }

    return group;
};*/

const stepManGroups: GroupFn[] = [
    ProcessSpGroup,
    TaskPermissionsGroup,
    TaskSpGroup,
    TaskMailsGroup,
];

const createGroups = (
    injector: PropertiesProvider['_injector'],
    element: Element,
    groups: GroupFn[]
): Groups => groups.map((group) => group(element, injector)).filter((group) => !!group);

const moveGroups = (element: Element, groups: Groups): void => {
    if (is(element, 'bpmn:Process')) {
        moveGroup(groups, 'spSiteListGroup', 1);
        // extend...
    }
    // extend...
};

const moveGroup = (groups: Groups, groupId: string, toIndex: number): void => {
    const groupIndex = groups.findIndex((group) => group.id === groupId);
    if (groupIndex < 0 || toIndex < 0 || groupIndex === toIndex) {
        return;
    }

    const [group] = groups.splice(groupIndex, 1);

    if (toIndex > groupIndex) {
        toIndex = toIndex - 1;
    }

    groups.splice(toIndex, 0, group);
};

export default PropertiesProvider;
